import {
  Button,
  Checkbox,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  authorizedFetch,
  cn,
} from '@yeshuman/sdk'
import type { CallBoardActionsProps } from '@yeshuman/sdk'
import { useCallback, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, Settings2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'

/** In Vite dev, default to 127.0.0.1:CALLS_E2E_LOCAL_PORT so ingest hits the same API/DB as Labs (see LABS_PROXY_TARGET). Production builds use tunnel when local_webhook_only is false. */
const DEFAULT_LOCAL_WEBHOOK_ONLY = import.meta.env.DEV

const DEFAULT_SIM: LiveBoardSimPayload = {
  skip_clear_db: false,
  calls: 15,
  start_spread_seconds: 60,
  turns_min: 3,
  turns_max: 6,
  turn_delay_min: 2,
  turn_delay_max: 5,
  post_status_delay_min: 0,
  post_status_delay_max: 0,
  pre_eoc_delay_min: 0,
  pre_eoc_delay_max: 0,
  assistant_lines: true,
  assistant_delay_min: 1,
  assistant_delay_max: 3,
  max_concurrent_calls: null,
  seed: null,
  dry_run: false,
  webhook_url: null,
  local_webhook_only: DEFAULT_LOCAL_WEBHOOK_ONLY,
}

export type LiveBoardSimPayload = {
  skip_clear_db: boolean
  calls: number
  start_spread_seconds: number
  turns_min: number
  turns_max: number
  turn_delay_min: number
  turn_delay_max: number
  post_status_delay_min: number
  post_status_delay_max: number
  pre_eoc_delay_min: number
  pre_eoc_delay_max: number
  assistant_lines: boolean
  assistant_delay_min: number
  assistant_delay_max: number
  max_concurrent_calls: number | null
  seed: number | null
  dry_run: boolean
  webhook_url: string | null
  local_webhook_only: boolean
}

export type LiveBoardSimHeaderActionsProps = {
  /** When set (e.g. from App header), buttons use the same chrome as other header controls. */
  headerButtonClassName?: string
}

function serializePayload(p: LiveBoardSimPayload): Record<string, unknown> {
  return {
    skip_clear_db: p.skip_clear_db,
    calls: p.calls,
    start_spread_seconds: p.start_spread_seconds,
    turns_min: p.turns_min,
    turns_max: p.turns_max,
    turn_delay_min: p.turn_delay_min,
    turn_delay_max: p.turn_delay_max,
    post_status_delay_min: p.post_status_delay_min,
    post_status_delay_max: p.post_status_delay_max,
    pre_eoc_delay_min: p.pre_eoc_delay_min,
    pre_eoc_delay_max: p.pre_eoc_delay_max,
    assistant_lines: p.assistant_lines,
    assistant_delay_min: p.assistant_delay_min,
    assistant_delay_max: p.assistant_delay_max,
    max_concurrent_calls: p.max_concurrent_calls,
    seed: p.seed,
    dry_run: p.dry_run,
    webhook_url: p.webhook_url,
    local_webhook_only: p.local_webhook_only,
  }
}

async function pollJob(jobId: string): Promise<'completed' | 'failed'> {
  const maxAttempts = 180
  for (let i = 0; i < maxAttempts; i++) {
    const res = await authorizedFetch(
      `/api/call-records/simulate-live-board/jobs/${jobId}`
    )
    if (!res.ok) {
      throw new Error(`Job status HTTP ${res.status}`)
    }
    const row = (await res.json()) as {
      status: string
      error?: string | null
    }
    if (row.status === 'completed') return 'completed'
    if (row.status === 'failed') {
      throw new Error(row.error || 'Simulation failed')
    }
    await new Promise((r) => setTimeout(r, 1000))
  }
  throw new Error('Simulation job timed out waiting for status')
}

const SIM_UI_ENABLED =
  String(import.meta.env?.VITE_CALLS_SIM_UI_ENABLED || '').toLowerCase() === 'true'

export function shouldShowLiveBoardSimActions(): boolean {
  return SIM_UI_ENABLED
}

type AdvancedFormState = {
  skip_clear_db: boolean
  calls: string
  start_spread_seconds: string
  turns_min: string
  turns_max: string
  turn_delay_min: string
  turn_delay_max: string
  post_status_delay_min: string
  post_status_delay_max: string
  pre_eoc_delay_min: string
  pre_eoc_delay_max: string
  assistant_lines: boolean
  assistant_delay_min: string
  assistant_delay_max: string
  max_concurrent_calls: string
  seed: string
  dry_run: boolean
  webhook_url: string
  local_webhook_only: boolean
}

function defaultsToForm(): AdvancedFormState {
  return {
    skip_clear_db: DEFAULT_SIM.skip_clear_db,
    calls: String(DEFAULT_SIM.calls),
    start_spread_seconds: String(DEFAULT_SIM.start_spread_seconds),
    turns_min: String(DEFAULT_SIM.turns_min),
    turns_max: String(DEFAULT_SIM.turns_max),
    turn_delay_min: String(DEFAULT_SIM.turn_delay_min),
    turn_delay_max: String(DEFAULT_SIM.turn_delay_max),
    post_status_delay_min: String(DEFAULT_SIM.post_status_delay_min),
    post_status_delay_max: String(DEFAULT_SIM.post_status_delay_max),
    pre_eoc_delay_min: String(DEFAULT_SIM.pre_eoc_delay_min),
    pre_eoc_delay_max: String(DEFAULT_SIM.pre_eoc_delay_max),
    assistant_lines: DEFAULT_SIM.assistant_lines,
    assistant_delay_min: String(DEFAULT_SIM.assistant_delay_min),
    assistant_delay_max: String(DEFAULT_SIM.assistant_delay_max),
    max_concurrent_calls: '',
    seed: '',
    dry_run: DEFAULT_SIM.dry_run,
    webhook_url: '',
    local_webhook_only: DEFAULT_LOCAL_WEBHOOK_ONLY,
  }
}

function formToPayload(f: AdvancedFormState): LiveBoardSimPayload {
  const maxConc = f.max_concurrent_calls.trim()
  const seedStr = f.seed.trim()
  const wh = f.webhook_url.trim()
  return {
    skip_clear_db: f.skip_clear_db,
    calls: Number(f.calls),
    start_spread_seconds: Number(f.start_spread_seconds),
    turns_min: Number(f.turns_min),
    turns_max: Number(f.turns_max),
    turn_delay_min: Number(f.turn_delay_min),
    turn_delay_max: Number(f.turn_delay_max),
    post_status_delay_min: Number(f.post_status_delay_min),
    post_status_delay_max: Number(f.post_status_delay_max),
    pre_eoc_delay_min: Number(f.pre_eoc_delay_min),
    pre_eoc_delay_max: Number(f.pre_eoc_delay_max),
    assistant_lines: f.assistant_lines,
    assistant_delay_min: Number(f.assistant_delay_min),
    assistant_delay_max: Number(f.assistant_delay_max),
    max_concurrent_calls: maxConc === '' ? null : Number(maxConc),
    seed: seedStr === '' ? null : Number(seedStr),
    dry_run: f.dry_run,
    webhook_url: wh === '' ? null : wh,
    local_webhook_only: f.local_webhook_only,
  }
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="text-xs font-semibold text-muted-foreground tracking-wide">
      {children}
    </div>
  )
}

export function LiveBoardSimHeaderActions({
  headerButtonClassName,
}: LiveBoardSimHeaderActionsProps) {
  const qc = useQueryClient()
  const [running, setRunning] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [advanced, setAdvanced] = useState<AdvancedFormState>(() => defaultsToForm())

  const runWithPayload = useCallback(
    async (payload: LiveBoardSimPayload) => {
      setRunning(true)
      try {
        const res = await authorizedFetch('/api/call-records/simulate-live-board', {
          method: 'POST',
          body: JSON.stringify(serializePayload(payload)),
        })
        if (res.status === 422) {
          const err = await res.json().catch(() => ({}))
          throw new Error(
            typeof err === 'object' && err && 'error' in err
              ? String((err as { error: string }).error)
              : 'Invalid simulation options'
          )
        }
        if (!res.ok) {
          const text = await res.text()
          throw new Error(text || `HTTP ${res.status}`)
        }
        const body = (await res.json()) as { job_id: string }
        toast.message('Demo simulation started', {
          description: `Job ${body.job_id.slice(0, 8)}… — data will appear across calls views.`,
        })
        try {
          await pollJob(body.job_id)
          toast.success('Demo simulation finished')
          await qc.invalidateQueries({ queryKey: ['call-records'] })
        } catch (pollErr) {
          toast.error(
            pollErr instanceof Error ? pollErr.message : 'Simulation job failed'
          )
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Request failed')
      } finally {
        setRunning(false)
      }
    },
    [qc]
  )

  if (!shouldShowLiveBoardSimActions()) {
    return null
  }

  const useHeaderChrome = Boolean(headerButtonClassName)
  const headerBtnCls = cn(
    'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-[var(--radius-button)] px-3 text-xs font-medium leading-none transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4',
    headerButtonClassName
  )

  const destructive =
    !advanced.skip_clear_db && !advanced.dry_run ? (
      <p className="text-warning text-xs font-medium leading-relaxed border border-warning/40 rounded-md p-4 bg-warning/10">
        By default this clears this tenant&apos;s call records, then ingests synthetic calls like
        real traffic. Data shows up on the live board, lists, and evaluations. Uses OpenAI for
        caller lines unless you enable dry run. Requires API{' '}
        <code className="text-xs">CALLS_SIM_HTTP_TRIGGER_ENABLED</code>.
      </p>
    ) : null

  const primaryControl = useHeaderChrome ? (
    <button
      type="button"
      disabled={running}
      className={headerBtnCls}
      title="Simulate demo call data for this tenant"
      onClick={() => runWithPayload(DEFAULT_SIM)}
    >
      {running ? (
        <Loader2 className="size-4 animate-spin shrink-0" aria-hidden />
      ) : (
        <Sparkles className="size-4 shrink-0" aria-hidden />
      )}
      <span className="max-w-[10rem] sm:max-w-none truncate">Simulate demo data</span>
    </button>
  ) : (
    <Button
      type="button"
      size="toolbarSm"
      variant="secondary"
      disabled={running}
      className="shrink-0"
      title="Simulate demo call data for this tenant"
      onClick={() => runWithPayload(DEFAULT_SIM)}
    >
      {running ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Sparkles className="size-4" />
      )}
      <span className="hidden sm:inline">Simulate demo data</span>
      <span className="sm:hidden sr-only">Simulate demo data</span>
    </Button>
  )

  const optionsTrigger = useHeaderChrome ? (
    <SheetTrigger asChild>
      <button
        type="button"
        className={headerBtnCls}
        title="Simulation options"
      >
        <Settings2 className="size-4 shrink-0" aria-hidden />
        <span className="hidden sm:inline">Options</span>
      </button>
    </SheetTrigger>
  ) : (
    <SheetTrigger asChild>
      <Button
        type="button"
        size="toolbarSm"
        variant="outline"
        className="shrink-0"
        title="Simulation options"
      >
        <Settings2 className="size-4" />
        <span className="hidden sm:inline">Options</span>
        <span className="sm:hidden sr-only">Options</span>
      </Button>
    </SheetTrigger>
  )

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 sm:gap-2">
      {primaryControl}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        {optionsTrigger}
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>Demo data simulation</SheetTitle>
            <SheetDescription>
              Generates parallel synthetic calls for this deployment (same behavior as{' '}
              <code className="text-xs">calls_simulate_live_board</code>). Affects the whole calls
              experience, not only the live board. In local dev, webhooks default to{' '}
              <code className="text-xs">127.0.0.1</code> (E2E port); turn off &quot;Local webhook
              only&quot; to send via your tunnel instead.
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-5 px-4 pb-2">
            {destructive}

            <div className="grid gap-3">
              <SectionLabel>Scope and safety</SectionLabel>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sim-skip-clear"
                    checked={advanced.skip_clear_db}
                    onCheckedChange={(v) =>
                      setAdvanced((s) => ({ ...s, skip_clear_db: Boolean(v) }))
                    }
                  />
                  <Label htmlFor="sim-skip-clear" className="font-normal">
                    Skip clear DB
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sim-dry"
                    checked={advanced.dry_run}
                    onCheckedChange={(v) =>
                      setAdvanced((s) => ({ ...s, dry_run: Boolean(v) }))
                    }
                  />
                  <Label htmlFor="sim-dry" className="font-normal">
                    Dry run (no HTTP / OpenAI)
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sim-local-wh"
                    checked={advanced.local_webhook_only}
                    onCheckedChange={(v) =>
                      setAdvanced((s) => ({ ...s, local_webhook_only: Boolean(v) }))
                    }
                  />
                  <Label htmlFor="sim-local-wh" className="font-normal">
                    Local webhook only (127.0.0.1)
                  </Label>
                </div>
              </div>
            </div>

            <div className="grid gap-3">
              <SectionLabel>Volume and timing</SectionLabel>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="sim-calls">Calls</Label>
                  <Input
                    id="sim-calls"
                    type="number"
                    value={advanced.calls}
                    onChange={(e) =>
                      setAdvanced((s) => ({ ...s, calls: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-spread">Start spread (s)</Label>
                  <Input
                    id="sim-spread"
                    type="number"
                    step="0.1"
                    value={advanced.start_spread_seconds}
                    onChange={(e) =>
                      setAdvanced((s) => ({
                        ...s,
                        start_spread_seconds: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-maxc">Max concurrent (optional)</Label>
                  <Input
                    id="sim-maxc"
                    type="number"
                    placeholder="—"
                    value={advanced.max_concurrent_calls}
                    onChange={(e) =>
                      setAdvanced((s) => ({
                        ...s,
                        max_concurrent_calls: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-seed">Seed (optional)</Label>
                  <Input
                    id="sim-seed"
                    type="number"
                    placeholder="—"
                    value={advanced.seed}
                    onChange={(e) =>
                      setAdvanced((s) => ({ ...s, seed: e.target.value }))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-3">
              <SectionLabel>Transcript and delays</SectionLabel>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sim-assistant"
                    checked={advanced.assistant_lines}
                    onCheckedChange={(v) =>
                      setAdvanced((s) => ({ ...s, assistant_lines: Boolean(v) }))
                    }
                  />
                  <Label htmlFor="sim-assistant" className="font-normal">
                    Assistant lines
                  </Label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="sim-tmin">Turns min</Label>
                  <Input
                    id="sim-tmin"
                    type="number"
                    value={advanced.turns_min}
                    onChange={(e) =>
                      setAdvanced((s) => ({ ...s, turns_min: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-tmax">Turns max</Label>
                  <Input
                    id="sim-tmax"
                    type="number"
                    value={advanced.turns_max}
                    onChange={(e) =>
                      setAdvanced((s) => ({ ...s, turns_max: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-tdmin">Turn delay min</Label>
                  <Input
                    id="sim-tdmin"
                    type="number"
                    step="0.1"
                    value={advanced.turn_delay_min}
                    onChange={(e) =>
                      setAdvanced((s) => ({ ...s, turn_delay_min: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-tdmax">Turn delay max</Label>
                  <Input
                    id="sim-tdmax"
                    type="number"
                    step="0.1"
                    value={advanced.turn_delay_max}
                    onChange={(e) =>
                      setAdvanced((s) => ({ ...s, turn_delay_max: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-psmin">Post-status delay min</Label>
                  <Input
                    id="sim-psmin"
                    type="number"
                    step="0.1"
                    value={advanced.post_status_delay_min}
                    onChange={(e) =>
                      setAdvanced((s) => ({
                        ...s,
                        post_status_delay_min: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-psmax">Post-status delay max</Label>
                  <Input
                    id="sim-psmax"
                    type="number"
                    step="0.1"
                    value={advanced.post_status_delay_max}
                    onChange={(e) =>
                      setAdvanced((s) => ({
                        ...s,
                        post_status_delay_max: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-pemin">Pre-EOC delay min</Label>
                  <Input
                    id="sim-pemin"
                    type="number"
                    step="0.1"
                    value={advanced.pre_eoc_delay_min}
                    onChange={(e) =>
                      setAdvanced((s) => ({
                        ...s,
                        pre_eoc_delay_min: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-pemax">Pre-EOC delay max</Label>
                  <Input
                    id="sim-pemax"
                    type="number"
                    step="0.1"
                    value={advanced.pre_eoc_delay_max}
                    onChange={(e) =>
                      setAdvanced((s) => ({
                        ...s,
                        pre_eoc_delay_max: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-admin">Assistant delay min</Label>
                  <Input
                    id="sim-admin"
                    type="number"
                    step="0.1"
                    value={advanced.assistant_delay_min}
                    onChange={(e) =>
                      setAdvanced((s) => ({
                        ...s,
                        assistant_delay_min: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sim-admax">Assistant delay max</Label>
                  <Input
                    id="sim-admax"
                    type="number"
                    step="0.1"
                    value={advanced.assistant_delay_max}
                    onChange={(e) =>
                      setAdvanced((s) => ({
                        ...s,
                        assistant_delay_max: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-3">
              <SectionLabel>Webhook override</SectionLabel>
              <div className="space-y-1">
                <Label htmlFor="sim-whurl">Webhook URL</Label>
                <Input
                  id="sim-whurl"
                  placeholder="Default from env"
                  value={advanced.webhook_url}
                  onChange={(e) =>
                    setAdvanced((s) => ({ ...s, webhook_url: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>

          <SheetFooter className="flex-col sm:flex-col gap-2">
            <Button
              type="button"
              disabled={running}
              className="w-full gap-1.5"
              onClick={async () => {
                const payload = formToPayload(advanced)
                setSheetOpen(false)
                await runWithPayload(payload)
              }}
            >
              {running ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Sparkles className="size-4" />
              )}
              Start simulation
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setAdvanced(defaultsToForm())}
            >
              Reset defaults
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  )
}

/** `calls.boardActions` slot: demo-traffic controls in the header, a hint on the empty board. */
export function LiveBoardSimActions({ placement }: CallBoardActionsProps) {
  if (placement === 'header') return <LiveBoardSimHeaderActions />
  if (!shouldShowLiveBoardSimActions()) return null
  return (
    <p>
      Use <span className="text-foreground font-medium">Simulate demo data</span> above to
      generate synthetic traffic for the board and dashboard.
    </p>
  )
}
